<template>
  <div class="workout-free-time">
    <v-card class="mb-3">
      <v-card-title class="d-flex flex-wrap align-center">
        <span>Свободное время залов</span>
        <v-spacer />
        <v-btn text color="primary" @click="setCurrentWindow">
          Текущие 24 часа
        </v-btn>
      </v-card-title>
    </v-card>

    <v-card class="mb-3">
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" md="4" lg="3">
            <date-picker
              v-model="selectedDate"
              label="Дата"
              outlined
              hide-details
              @input="onDateChange"
            />
          </v-col>

          <v-col cols="12" md="8" lg="9" class="d-flex flex-wrap align-center">
            <div class="text-body-2 mr-4 mb-2 mb-md-0">
              Интервал: {{ formatTimelineDate(rangeStart) }} - {{ formatTimelineDate(rangeEnd) }}
            </div>

            <v-btn color="primary" :loading="loading" @click="fetchTimeline">
              Обновить
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert v-if="errorMessage" type="error" class="mb-3" outlined>
      {{ errorMessage }}
    </v-alert>

    <v-row dense class="mb-3">
      <v-col v-for="summaryItem in summaryItems" :key="summaryItem.label" cols="12" sm="6" lg="3">
        <v-card outlined class="summary-card">
          <v-card-text>
            <div class="text-caption grey--text text--lighten-1">{{ summaryItem.label }}</div>
            <div class="text-subtitle-1 font-weight-medium">{{ summaryItem.value }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="mb-3">
      <v-card-title class="d-flex flex-wrap align-center">
        <span>Шкала занятости</span>
        <v-spacer />
        <div class="timeline-legend">
          <span class="timeline-legend__item">
            <span class="timeline-legend__swatch timeline-legend__swatch--free"></span>
            Свободно
          </span>
          <span class="timeline-legend__item">
            <span class="timeline-legend__swatch timeline-legend__swatch--busy"></span>
            Занято
          </span>
        </div>
      </v-card-title>

      <v-card-text>
        <div class="timeline-scale">
          <div
            v-for="marker in hourMarkers"
            :key="marker.id"
            class="timeline-scale__marker"
            :style="{ left: marker.position }"
          >
            <span>{{ marker.label }}</span>
          </div>
        </div>

        <div class="timeline-bar" :class="{ 'timeline-bar--loading': loading }">
          <div
            v-for="interval in freeIntervals"
            :key="`free_${interval.start}_${interval.end}`"
            class="timeline-bar__segment timeline-bar__segment--free"
            :style="getSegmentStyle(interval)"
          ></div>
          <div
            v-for="interval in occupiedIntervals"
            :key="`busy_${interval.start}_${interval.end}`"
            class="timeline-bar__segment timeline-bar__segment--busy"
            :style="getSegmentStyle(interval)"
          ></div>
        </div>
      </v-card-text>
    </v-card>

    <v-row dense>
      <v-col cols="12" lg="6">
        <v-card outlined class="interval-card">
          <v-card-title>Свободные интервалы</v-card-title>
          <v-card-text class="pt-0">
            <div v-if="!freeIntervals.length" class="grey--text text--lighten-1">
              Свободного времени в выбранном окне нет.
            </div>

            <div v-for="interval in freeIntervals" :key="`free_list_${interval.start}_${interval.end}`" class="interval-row">
              <div class="font-weight-medium">{{ formatInterval(interval) }}</div>
              <div class="grey--text text--lighten-1">{{ formatDuration(interval) }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card outlined class="interval-card">
          <v-card-title>Занятые интервалы</v-card-title>
          <v-card-text class="pt-0">
            <div v-if="!occupiedIntervals.length" class="grey--text text--lighten-1">
              Бронирований в выбранном окне нет.
            </div>

            <div
              v-for="interval in occupiedIntervals"
              :key="`busy_list_${interval.start}_${interval.end}`"
              class="interval-row"
            >
              <div class="font-weight-medium">{{ formatInterval(interval) }}</div>
              <div class="grey--text text--lighten-1">{{ formatDuration(interval) }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from "moment";
import { DatePicker } from "@/components/UI";

const WINDOW_SECONDS = 24 * 60 * 60;

export default {
  name: "workout-free-time",

  components: {
    DatePicker,
  },

  data() {
    const now = moment().unix();

    return {
      loading: false,
      errorMessage: "",
      gyms: [],
      selectedDate: moment.unix(now).startOf("day").unix(),
      windowMode: "current",
      currentWindowStart: now,
      occupiedIntervals: [],
      freeIntervals: [],
      bookedIntervalCount: 0,
    };
  },

  computed: {
    rangeStart() {
      if (this.windowMode === "current") {
        return this.currentWindowStart;
      }

      return moment.unix(this.selectedDate).startOf("day").unix();
    },

    rangeEnd() {
      return this.rangeStart + WINDOW_SECONDS;
    },

    summaryItems() {
      return [
        { label: "Залов", value: this.gyms.length.toLocaleString("ru") },
        { label: "Интервалов занятости", value: this.bookedIntervalCount.toLocaleString("ru") },
        { label: "Занято", value: this.formatMinutes(this.getTotalMinutes(this.occupiedIntervals)) },
        { label: "Свободно", value: this.formatMinutes(this.getTotalMinutes(this.freeIntervals)) },
      ];
    },

    hourMarkers() {
      const markers = [];

      for (let hour = 0; hour <= 24; hour += 1) {
        const timestamp = this.rangeStart + hour * 60 * 60;
        const position = `${(hour / 24) * 100}%`;

        markers.push({
          id: `${hour}_${timestamp}`,
          label: moment.unix(timestamp).format("HH:mm"),
          position,
        });
      }

      return markers;
    },
  },

  created() {
    this.fetchTimeline();
  },

  methods: {
    async fetchGyms() {
      const result = await this.$api.gym.gyms();

      this.gyms = result.data?.data?.gyms ?? [];
    },

    async fetchTimeline() {
      this.loading = true;
      this.errorMessage = "";

      try {
        if (!this.gyms.length) {
          await this.fetchGyms();
        }

        const gymIds = this.gyms.map((gym) => gym.id);
        const result = await this.$api.gym.getBookedTimeForGyms(
          gymIds,
          this.rangeStart,
          this.rangeEnd
        );

        if (result.data?.errors?.length) {
          throw new Error(result.data.errors[0].message);
        }

        const responseData = result.data?.data ?? {};
        const intervals = Object.keys(responseData).reduce((accumulator, key) => {
          const gymIntervals = responseData[key] ?? [];
          accumulator.push(...gymIntervals);
          return accumulator;
        }, []);

        this.occupiedIntervals = this.mergeIntervals(
          intervals.map((item) => this.normalizeInterval(item)).filter(Boolean)
        );
        this.bookedIntervalCount = this.occupiedIntervals.length;
        this.freeIntervals = this.buildFreeIntervals(this.occupiedIntervals);
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.errors?.[0]?.message ||
          error?.message ||
          "Не удалось получить интервалы бронирований.";
        this.occupiedIntervals = [];
        this.freeIntervals = this.buildFreeIntervals([]);
        this.bookedIntervalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    onDateChange() {
      this.windowMode = "day";
      this.fetchTimeline();
    },

    setCurrentWindow() {
      this.windowMode = "current";
      this.currentWindowStart = moment().unix();
      this.selectedDate = moment.unix(this.currentWindowStart).startOf("day").unix();
      this.fetchTimeline();
    },

    normalizeInterval(item) {
      const start = Math.max(Number(item.date_start), this.rangeStart);
      const end = Math.min(Number(item.date_end), this.rangeEnd);

      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        return null;
      }

      return { start, end };
    },

    mergeIntervals(intervals) {
      if (!intervals.length) {
        return [];
      }

      const sortedIntervals = [...intervals].sort((a, b) => a.start - b.start);

      return sortedIntervals.reduce((merged, interval) => {
        const lastInterval = merged[merged.length - 1];

        if (!lastInterval) {
          merged.push({ ...interval });
          return merged;
        }

        if (interval.start <= lastInterval.end) {
          lastInterval.end = Math.max(lastInterval.end, interval.end);
          return merged;
        }

        merged.push({ ...interval });
        return merged;
      }, []);
    },

    buildFreeIntervals(occupiedIntervals) {
      const freeIntervals = [];
      let cursor = this.rangeStart;

      occupiedIntervals.forEach((interval) => {
        if (interval.start > cursor) {
          freeIntervals.push({
            start: cursor,
            end: interval.start,
          });
        }

        cursor = Math.max(cursor, interval.end);
      });

      if (cursor < this.rangeEnd) {
        freeIntervals.push({
          start: cursor,
          end: this.rangeEnd,
        });
      }

      return freeIntervals;
    },

    getSegmentStyle(interval) {
      const totalDuration = this.rangeEnd - this.rangeStart;
      const left = ((interval.start - this.rangeStart) / totalDuration) * 100;
      const width = ((interval.end - interval.start) / totalDuration) * 100;

      return {
        left: `${left}%`,
        width: `${Math.max(width, 0.3)}%`,
      };
    },

    getTotalMinutes(intervals) {
      return intervals.reduce(
        (total, interval) => total + Math.round((interval.end - interval.start) / 60),
        0
      );
    },

    formatMinutes(value) {
      const hours = Math.floor(value / 60);
      const minutes = value % 60;

      if (!hours) {
        return `${minutes} мин`;
      }

      if (!minutes) {
        return `${hours} ч`;
      }

      return `${hours} ч ${minutes} мин`;
    },

    formatTimelineDate(timestamp) {
      return moment.unix(timestamp).format("DD.MM.YYYY HH:mm");
    },

    formatInterval(interval) {
      return `${this.formatTimelineDate(interval.start)} - ${this.formatTimelineDate(interval.end)}`;
    },

    formatDuration(interval) {
      return this.formatMinutes(Math.round((interval.end - interval.start) / 60));
    },
  },
};
</script>

<style scoped>
.summary-card,
.interval-card {
  height: 100%;
}

.timeline-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.timeline-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
}

.timeline-legend__swatch {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.timeline-legend__swatch--free {
  background: #2e7d32;
}

.timeline-legend__swatch--busy {
  background: #c62828;
}

.timeline-scale {
  position: relative;
  height: 28px;
  margin-bottom: 8px;
}

.timeline-scale__marker {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}

.timeline-bar {
  position: relative;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.timeline-bar--loading {
  opacity: 0.7;
}

.timeline-bar__segment {
  position: absolute;
  top: 0;
  bottom: 0;
}

.timeline-bar__segment--free {
  background: rgba(76, 175, 80, 0.55);
}

.timeline-bar__segment--busy {
  background: rgba(244, 67, 54, 0.72);
}

.interval-row + .interval-row {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

@media (max-width: 960px) {
  .timeline-scale {
    overflow-x: auto;
    overflow-y: hidden;
  }
}
</style>
